import { Dispatch } from 'react'
import * as Sentry from '@sentry/react'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { fetchAuthInfo } from '../auth-info'
import { authInfoErrorToOutcome, authInfoToOutcome } from '../outcomes'
import { setSignupEmail, trackSignup, trackSignupStarted } from '../analytics'
import { EmailOutcome, NewSignupAction } from '../types'
import { ISignupModals } from './use-signup-modals'
import { ISignupNavigation } from './use-signup-navigation'

export interface IUseEmailSignupParams {
  localDispatch: Dispatch<NewSignupAction>
  modals: ISignupModals
  navigation: ISignupNavigation
}

export interface IEmailSignup {
  submitEmail: (email: string) => Promise<void>
}

const isDisposableDomain = async (email: string): Promise<boolean> => {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return false

  // Imported on demand: the list is large and most visitors never reach this check.
  const { blacklistDomains } = await import('@vacationtracker/shared/data/blacklist-domain')
  return blacklistDomains.some((blacklisted) => blacklisted.toLowerCase() === domain)
}

export const useEmailSignup = ({ localDispatch, modals, navigation }: IUseEmailSignupParams): IEmailSignup => {
  const applyOutcome = (outcome: EmailOutcome, email: string) => {
    localDispatch({ type: 'emailResolved', outcome, email })
    trackSignup('SIGNUP_EMAIL_LOOKUP_RESULT', { outcome: outcome.kind })
    if (outcome.kind === 'unknownError' || outcome.kind === 'invalidEmail') {
      trackSignup('SIGNUP_WITH_EMAIL_ERROR', { platform: PlatformEnum.Email, outcome: outcome.kind })
    }

    switch (outcome.kind) {
      // One address can reach an account on more than one platform, and a modal offering the
      // single platform the lookup happened to name is no use to someone whose account is on
      // the other one. The sign-in page can offer all of them, so the answer goes there.
      case 'existsOnOtherPlatform':
        trackSignup('SIGNUP_EXISTING_ACCOUNT_SENT_TO_SIGNIN', {
          platform: outcome.platform,
          availablePlatforms: outcome.authInfo.availablePlatforms,
        })
        navigation.goToLoginWithAccount({ email, authInfo: outcome.authInfo })
        return
      // Signing up cannot fix a deactivated account, and the page that can ask for it back is
      // the sign-in page, which owns the request and everything that follows it.
      case 'inactive':
        trackSignup('SIGNUP_INACTIVE_ACCOUNT_SENT_TO_SIGNIN')
        navigation.goToLoginWithAccount({ email, inactive: true })
        return
      case 'domainSuggestsPlatform':
        modals.showSignupWithAnotherPlatform(outcome.domainPlatform)
        return
      case 'newAccount':
        navigation.goToPersonalInfo(email)
        return
      default:
        // The rest are answered by the banner the reducer just set.
        return
    }
  }

  const submitEmail = async (email: string) => {
    localDispatch({ type: 'submitted', source: PlatformEnum.Email })
    setSignupEmail(email)
    // Email opens its funnel the same way the three platform buttons open theirs.
    trackSignup('SIGNUP_WITH_EMAIL_BUTTON_CLICKED', { platform: PlatformEnum.Email })

    // Counted only once the address is one we would accept, so a disposable domain is no
    // longer reported as a started sign-up.
    if (await isDisposableDomain(email)) {
      localDispatch({ type: 'disposableEmailRejected' })
      trackSignup('SIGNUP_EMAIL_LOOKUP_RESULT', { outcome: 'disposableEmail' })
      trackSignup('SIGNUP_WITH_EMAIL_ERROR', { platform: PlatformEnum.Email, outcome: 'disposableEmail' })
      return
    }

    // The sign-in page and the forgot-password modal both prefill from this key, so a
    // disposable address never reaches it.
    sessionStorage.setItem('vtUserEmail', email)
    trackSignupStarted(PlatformEnum.Email)
    trackSignup('SIGNUP_EMAIL_LOOKUP_SUBMITTED')

    try {
      const response = await fetchAuthInfo(email)
      applyOutcome(authInfoToOutcome(response), email)
    } catch (error) {
      const outcome = authInfoErrorToOutcome(error)
      if (outcome.kind === 'unknownError') {
        Sentry.captureException(error)
      }
      applyOutcome(outcome, email)
    }
  }

  return { submitEmail }
}
